import { useState, useEffect } from "react"
import { HelpCircle, X, Lightbulb, Clock, Smartphone, Monitor, BookOpen, ChevronRight } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { usePersonalization } from "./PersonalizationEngine"
import { useAuth } from "@/hooks/useAuth"

interface HelpTip {
  id: string
  title: string
  content: string
  type: 'feature' | 'shortcut' | 'tip' | 'accessibility'
  priority: number
  conditions: {
    device_types?: string[]
    user_types?: string[]
    time_sensitive?: boolean
    first_time_user?: boolean
  }
}

const helpTips: HelpTip[] = [
  {
    id: 'search_filters',
    title: 'Filter nutzen',
    content: 'Verwenden Sie die erweiterten Filter, um schnell die perfekte Unterkunft zu finden. Sie können nach Preis, Ausstattung und Standort filtern.',
    type: 'feature',
    priority: 1,
    conditions: { first_time_user: true }
  },
  {
    id: 'mobile_shortcuts',
    title: 'Mobile Shortcuts',
    content: 'Wischen Sie auf Mobilgeräten nach links oder rechts, um zwischen Unterkünften zu navigieren.',
    type: 'shortcut',
    priority: 2,
    conditions: { device_types: ['mobile'] }
  },
  {
    id: 'accessibility_features',
    title: 'Barrierefreiheit',
    content: 'Drücken Sie Alt+A für erweiterte Barrierefreiheitsoptionen oder Alt+H für Hilfe.',
    type: 'accessibility',
    priority: 1,
    conditions: {}
  },
  {
    id: 'evening_booking',
    title: 'Abends buchen',
    content: 'Viele Hosts bestätigen Buchungen am Abend. Ihre Chancen für eine schnelle Bestätigung sind jetzt besonders gut!',
    type: 'tip',
    priority: 3,
    conditions: { time_sensitive: true }
  },
  {
    id: 'desktop_view_modes',
    title: 'Ansichtsmodi',
    content: 'Auf Desktop-Geräten können Sie zwischen Karten- und Listenansicht wechseln für bessere Übersicht.',
    type: 'feature',
    priority: 2,
    conditions: { device_types: ['desktop', 'tablet'] }
  },
  {
    id: 'save_preferences',
    title: 'Einstellungen speichern',
    content: 'Ihre Suchpräferenzen werden automatisch gespeichert, um Ihnen beim nächsten Besuch passende Vorschläge zu machen.',
    type: 'feature',
    priority: 3,
    conditions: {}
  }
]

export const AdaptiveHelp = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [dismissedTips, setDismissedTips] = useState<string[]>([])
  const [currentTip, setCurrentTip] = useState<HelpTip | null>(null)
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const [permanentlyDisabled, setPermanentlyDisabled] = useState(false)
  
  const { preferences, trackInteraction } = usePersonalization()
  const { user } = useAuth()

  // Load permanently disabled state
  useEffect(() => {
    const disabled = localStorage.getItem('adaptive-help-disabled')
    setPermanentlyDisabled(disabled === 'true')
  }, [])

  // Get relevant tips based on current context
  const getRelevantTips = () => {
    if (!preferences || permanentlyDisabled) return []

    const currentHour = new Date().getHours()
    const isEvening = currentHour >= 18 || currentHour <= 6
    const isFirstTimeUser = !user || !preferences.search_history.length

    return helpTips
      .filter(tip => {
        // Check if tip was dismissed
        if (dismissedTips.includes(tip.id)) return false

        // Check device type condition
        if (tip.conditions.device_types && !tip.conditions.device_types.includes(preferences.device_type)) {
          return false
        }

        // Check first time user condition
        if (tip.conditions.first_time_user && !isFirstTimeUser) {
          return false
        }

        // Check time sensitive condition
        if (tip.conditions.time_sensitive && !isEvening) {
          return false
        }

        return true
      })
      .sort((a, b) => a.priority - b.priority)
  }

  // Show contextual tip
  useEffect(() => {
    const relevantTips = getRelevantTips()
    if (relevantTips.length > 0 && !currentTip) {
      setCurrentTip(relevantTips[0])
    }
  }, [preferences, dismissedTips, currentTip])

  const dismissTip = (tipId: string) => {
    setDismissedTips(prev => [...prev, tipId])
    if (currentTip?.id === tipId) {
      setCurrentTip(null)
    }
    trackInteraction('help_tip_dismissed', { tip_id: tipId })
  }

  const disablePermanently = () => {
    localStorage.setItem('adaptive-help-disabled', 'true')
    setPermanentlyDisabled(true)
    setCurrentTip(null)
    trackInteraction('help_tips_disabled_permanently')
  }

  const openHelp = () => {
    setIsOpen(true)
    trackInteraction('help_opened')
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    )
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'feature': return <BookOpen className="w-4 h-4" />
      case 'shortcut': return <Smartphone className="w-4 h-4" />
      case 'tip': return <Lightbulb className="w-4 h-4" />
      case 'accessibility': return <HelpCircle className="w-4 h-4" />
      default: return <HelpCircle className="w-4 h-4" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'feature': return 'Feature'
      case 'shortcut': return 'Shortcut'
      case 'tip': return 'Tipp'
      case 'accessibility': return 'Barrierefreiheit'
      default: return 'Hilfe'
    }
  }

  const groupedTips = getRelevantTips().reduce((acc, tip) => {
    if (!acc[tip.type]) acc[tip.type] = []
    acc[tip.type].push(tip)
    return acc
  }, {} as Record<string, HelpTip[]>)

  return (
    <>
      {/* Contextual Help Tip */}
      {currentTip && !isOpen && (
        <div className="fixed bottom-28 right-4 z-40 max-w-sm md:bottom-32 md:right-6">
          <Card className="border-primary shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getIcon(currentTip.type)}
                  <CardTitle className="text-sm">
                    {currentTip.title}
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {getTypeLabel(currentTip.type)}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissTip(currentTip.id)}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-3">
                {currentTip.content}
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => dismissTip(currentTip.id)}
                  className="flex-1"
                >
                  Verstanden
                </Button>
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={disablePermanently}
                  className="text-xs px-2"
                  title="Hilfe-Tipps dauerhaft deaktivieren"
                >
                  Nicht mehr anzeigen
                </Button>
                <Button 
                  size="sm"
                  onClick={openHelp}
                  className="flex-1"
                >
                  Mehr Hilfe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Help Button */}
      <div className="fixed bottom-16 left-4 z-40 md:bottom-20 md:left-4">
        <Button
          onClick={openHelp}
          size="lg"
          variant="outline"
          className="rounded-full shadow-lg bg-background hover:shadow-xl transition-all duration-200"
        >
          <HelpCircle className="w-5 h-5 mr-2" />
          Hilfe
        </Button>
      </div>

      {/* Help Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Adaptive Hilfe
                  </CardTitle>
                  <CardDescription>
                    Personalisierte Tipps für Ihre aktuelle Situation
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Context Info */}
              <div className="mb-6 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {preferences?.device_type === 'mobile' && <Smartphone className="w-4 h-4" />}
                  {preferences?.device_type === 'desktop' && <Monitor className="w-4 h-4" />}
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Ihr Kontext:</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {preferences?.device_type === 'mobile' && "Mobile Ansicht - "}
                  {preferences?.device_type === 'desktop' && "Desktop Ansicht - "}
                  {new Date().getHours() >= 18 || new Date().getHours() <= 6 ? "Abends" : "Tagsüber"} • 
                  {user ? " Angemeldet" : " Gast"}
                </p>
              </div>

              {/* Grouped Tips */}
              <div className="space-y-4">
                {Object.entries(groupedTips).map(([type, tips]) => (
                  <Collapsible 
                    key={type}
                    open={expandedSections.includes(type)}
                    onOpenChange={() => toggleSection(type)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                        <div className="flex items-center gap-2">
                          {getIcon(type)}
                          <span className="font-medium">{getTypeLabel(type)}</span>
                          <Badge variant="secondary">{tips.length}</Badge>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform ${
                          expandedSections.includes(type) ? 'rotate-90' : ''
                        }`} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 mt-2">
                      {tips.map(tip => (
                        <div key={tip.id} className="border rounded-lg p-3 bg-muted/50">
                          <h4 className="font-medium text-sm mb-1">{tip.title}</h4>
                          <p className="text-sm text-muted-foreground">{tip.content}</p>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>

              {Object.keys(groupedTips).length === 0 && (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">Keine neuen Tipps</h3>
                  <p className="text-sm text-muted-foreground">
                    Sie kennen bereits alle relevanten Funktionen für Ihre aktuelle Situation.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}